import { useEffect, useMemo, useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';

type StatusType = {
  name: string;
  code: string;
  description?: string;
  color?: string;
  active: boolean;
};

const getImageUrl = (
  filename: string | null | undefined
) => {
  if (!filename) {
    return null;
  }

  // Already a complete URL
  if (
    filename.startsWith('http://') ||
    filename.startsWith('https://')
  ) {
    return filename;
  }

  return `https://guardiancloudcare.com/backend/uploads/${encodeURIComponent(
    filename
  )}`;
};

export default function SWChildMonitoringPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [childrenData, setChildrenData] = useState<any[]>([]);
  const [selectedChild, setSelectedChild] =
    useState<any>(null);

  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(true);

  // =========================================================
  // ADMIN CONFIGURED STATUS TYPES
  // =========================================================

  const [statusTypes, setStatusTypes] = useState<
    StatusType[]
  >([]);

  const [loadingStatusTypes, setLoadingStatusTypes] =
    useState(true);

  // =========================================================
  // NORMALIZE STATUS
  // =========================================================

  const normalizeStatus = (status?: string) => {
    return String(status || '')
      .trim()
      .toLowerCase();
  };

  // =========================================================
  // FIND CONFIGURED STATUS
  // =========================================================

  const getConfiguredStatus = (
    status?: string
  ): StatusType | null => {
    if (!status) {
      return null;
    }

    const normalized = normalizeStatus(status);

    return (
      statusTypes.find(
        (item) =>
          normalizeStatus(item.name) === normalized ||
          normalizeStatus(item.code) === normalized
      ) || null
    );
  };

  // =========================================================
  // CHECK IF STATUS IS CLOSED
  // =========================================================

  const isClosedStatus = (status?: string) => {
    const configuredStatus =
      getConfiguredStatus(status);

    if (!configuredStatus) {
      return false;
    }

    const normalized = normalizeStatus(
      configuredStatus.name
    );

    return (
      normalized.includes('closed') ||
      normalized.includes('complete') ||
      normalized.includes('resolved')
    );
  };

  // =========================================================
  // FETCH CHILD MONITORING
  // =========================================================

  const fetchMonitoring = async () => {
    if (!user?.uid) {
      console.error('No logged-in user UID');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const url =
        `https://guardiancloudcare.com/backend/api/get_child_monitoring.php?uid=${encodeURIComponent(
          user.uid
        )}`;

      console.log(
        'Fetching child monitoring:',
        url
      );

      const response = await fetch(url);

      const result = await response.json();

      console.log(
        'CHILD MONITORING RESPONSE:',
        result
      );

      if (!response.ok) {
        throw new Error(
          result?.error ||
            `HTTP Error ${response.status}`
        );
      }

      
      if (Array.isArray(result)) {
          const uniqueChildren = Array.from(
              new Map(
                  result.map((child: any) => [
                      child.case_number,
                      child
                  ])
              ).values()
          );

          setChildrenData(uniqueChildren);

          setSelectedChild((current: any) => {
              if (!uniqueChildren.length) {
                  return null;
              }

              if (!current) {
                  return uniqueChildren[0];
              }

              const existingChild = uniqueChildren.find(
                  (child: any) =>
                      child.case_number === current.case_number
              );

              return existingChild || uniqueChildren[0];
          });
      } else if (result?.success === false) {
        throw new Error(
          result.error ||
            'Failed to fetch child monitoring data.'
        );
      } else {
        console.error(
          'Unexpected child monitoring response:',
          result
        );

        setChildrenData([]);
        setSelectedChild(null);
      }
    } catch (error) {
      console.error(
        'Child monitoring fetch error:',
        error
      );

      setChildrenData([]);
      setSelectedChild(null);
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // LOAD CHILD MONITORING
  // =========================================================

  useEffect(() => {
    if (user?.uid) {
      fetchMonitoring();
    }
  }, [user?.uid]);

  // =========================================================
  // LOAD ADMIN STATUS TYPES
  // =========================================================

  useEffect(() => {
    const loadStatusTypes = async () => {
      try {
        setLoadingStatusTypes(true);

        const response = await fetch(
          'https://guardiancloudcare.com/backend/api/get_system_config.php'
        );

        if (!response.ok) {
          throw new Error(
            `HTTP error ${response.status}`
          );
        }

        const result = await response.json();

        if (
          !result.success ||
          !Array.isArray(result.data)
        ) {
          throw new Error(
            result.error ||
              'Invalid system configuration response'
          );
        }

        const statusConfig = result.data.find(
          (config: any) =>
            config.config_key === 'status_types'
        );

        if (!statusConfig) {
          setStatusTypes([]);
          return;
        }

        const statuses: StatusType[] =
          Array.isArray(
            statusConfig.config_value
          )
            ? statusConfig.config_value
                .map((item: any) => {
                  // Support old string format
                  if (typeof item === 'string') {
                    return {
                      name: item,
                      code: item
                        .toUpperCase()
                        .replace(/[^A-Z0-9]+/g, '_')
                        .replace(
                          /^_+|_+$/g,
                          ''
                        ),
                      description: '',
                      color: '',
                      active: true,
                    };
                  }

                  // Support current object format
                  return {
                    name: item.name || '',
                    code:
                      item.code ||
                      String(item.name || '')
                        .toUpperCase()
                        .replace(
                          /[^A-Z0-9]+/g,
                          '_'
                        )
                        .replace(
                          /^_+|_+$/g,
                          ''
                        ),
                    description:
                      item.description || '',
                    color: item.color || '',
                    active:
                      item.active !== false,
                  };
                })
                .filter(
                  (status: StatusType) =>
                    status.name &&
                    status.active
                )
            : [];

        console.log(
          'ACTIVE ADMIN STATUS TYPES:',
          statuses
        );

        setStatusTypes(statuses);
      } catch (error) {
        console.error(
          'Failed to load status types:',
          error
        );

        setStatusTypes([]);
      } finally {
        setLoadingStatusTypes(false);
      }
    };

    loadStatusTypes();
  }, []);

  // =========================================================
  // HELPERS
  // =========================================================

  const getFullName = (child: any) => {
    return `${child?.first_name || ''} ${
      child?.middle_name || ''
    } ${child?.last_name || ''}`
      .replace(/\s+/g, ' ')
      .trim() || 'No Name';
  };

  const getInitials = (child: any) => {
    const first =
      child?.first_name?.charAt(0) || '';

    const last =
      child?.last_name?.charAt(0) || '';

    return `${first}${last}` || 'NA';
  };

  // =========================================================
  // RISK
  // =========================================================

  const getRisk = (type: string) => {
    let score = 75;

    switch (type) {
      case 'Sexual Abuse':
      case 'Child Trafficking':
      case 'Online Sexual Exploitation':
        score = 95;
        break;

      case 'Physical Abuse':
        score = 90;
        break;

      case 'Emotional Abuse':
        score = 80;
        break;

      case 'Neglect':
      case 'Child Labor':
      case 'Abandonment':
        score = 75;
        break;

      default:
        score = 70;
    }

    let level = 'low';

    if (score >= 90) {
      level = 'critical';
    } else if (score >= 80) {
      level = 'high';
    } else if (score >= 75) {
      level = 'moderate';
    }

    return {
      riskScore: score,
      riskLevel: level,
    };
  };

  const getRiskColor = (risk: string) => {
    const r = (risk || '').toLowerCase();

    if (r === 'critical') {
      return 'bg-red-100 text-red-700';
    }

    if (r === 'high') {
      return 'bg-orange-100 text-orange-700';
    }

    if (r === 'moderate') {
      return 'bg-yellow-100 text-yellow-700';
    }

    return 'bg-gray-100 text-gray-600';
  };

  // =========================================================
  // STATUS COLOR
  // =========================================================

  const getStatusColor = (status?: string) => {
    const configuredStatus =
      getConfiguredStatus(status);

    const normalized = normalizeStatus(
      configuredStatus?.name || status
    );

    // Use Admin configured color if available
    const configuredColor =
      configuredStatus?.color?.trim();

    if (configuredColor) {
      return configuredColor;
    }

    // Fallback colors based on status meaning
    if (
      normalized.includes('closed') ||
      normalized.includes('complete') ||
      normalized.includes('resolved')
    ) {
      return 'bg-green-100 text-green-700';
    }

    if (
      normalized.includes('ongoing') ||
      normalized.includes('progress') ||
      normalized.includes('review') ||
      normalized.includes('approval') ||
      normalized.includes('escalated')
    ) {
      return 'bg-blue-100 text-blue-700';
    }

    if (
      normalized.includes('open') ||
      normalized.includes('pending')
    ) {
      return 'bg-yellow-100 text-yellow-700';
    }

    return 'bg-gray-100 text-gray-700';
  };

  // =========================================================
  // FILTER CHILDREN
  // =========================================================

  const filteredChildren = useMemo(() => {
    return childrenData.filter((child) => {
      const keyword =
        search.toLowerCase().trim();

      const fullName =
        getFullName(child).toLowerCase();

      const caseNumber =
        (
          child.case_number || ''
        ).toLowerCase();

      const incident =
        (
          child.incident_type || ''
        ).toLowerCase();

      const matchSearch =
        fullName.includes(keyword) ||
        caseNumber.includes(keyword) ||
        incident.includes(keyword);

      const status =
        normalizeStatus(
          child.case_status
        );

      const risk =
        getRisk(
          child.incident_type
        ).riskLevel;

      // -------------------------------------------------------
      // ACTIVE TAB
      // -------------------------------------------------------

      const matchActive =
        activeTab === 'active' &&
        statusTypes.some(
          (configuredStatus) =>
            normalizeStatus(
              configuredStatus.name
            ) === status ||
            normalizeStatus(
              configuredStatus.code
            ) === status
        ) &&
        !isClosedStatus(
          child.case_status
        );

      // -------------------------------------------------------
      // CLOSED TAB
      // -------------------------------------------------------

      const matchClosed =
        activeTab === 'closed' &&
        statusTypes.some(
          (configuredStatus) =>
            normalizeStatus(
              configuredStatus.name
            ) === status ||
            normalizeStatus(
              configuredStatus.code
            ) === status
        ) &&
        isClosedStatus(
          child.case_status
        );

      // -------------------------------------------------------
      // TAB MATCH
      // -------------------------------------------------------

      const matchTab =
        activeTab === 'all' ||
        matchActive ||
        (activeTab === 'risk' &&
          ['critical', 'high'].includes(
            risk
          )) ||
        matchClosed;

      return (
        matchSearch &&
        matchTab
      );
    });
  }, [
    childrenData,
    search,
    activeTab,
    statusTypes,
  ]);

  // =========================================================
  // STATISTICS
  // =========================================================

  const totalChildren =
    childrenData.length;

  const activeCases =
    childrenData.filter((child) => {
      const status =
        getConfiguredStatus(
          child.case_status
        );

      // If status isn't configured,
      // don't count it as an active configured status.
      if (!status) {
        return false;
      }

      return !isClosedStatus(
        child.case_status
      );
    }).length;

  const atRisk =
    childrenData.filter((child) => {
      const risk =
        getRisk(
          child.incident_type
        ).riskLevel;

      return [
        'critical',
        'high',
      ].includes(risk);
    }).length;

  const closedCases =
    childrenData.filter((child) => {
      return isClosedStatus(
        child.case_status
      );
    }).length;

  const victimReportsCount =
    childrenData.reduce(
      (total, child) => {
        return (
          total +
          (
            child.victim_reports
              ?.length || 0
          )
        );
      },
      0
    );

  // =========================================================
  // DETERMINE IF CLOSED TAB SHOULD BE SHOWN
  // =========================================================

  const hasActiveClosedStatus =
    statusTypes.some((status) => {
      const normalized =
        normalizeStatus(
          status.name
        );

      return (
        normalized.includes('closed') ||
        normalized.includes('complete') ||
        normalized.includes('resolved')
      );
    });

  // If current tab becomes unavailable
  // because Admin disabled the status,
  // return to All.
  useEffect(() => {
    if (
      activeTab === 'closed' &&
      !hasActiveClosedStatus
    ) {
      setActiveTab('all');
    }
  }, [
    activeTab,
    hasActiveClosedStatus,
  ]);

  // =========================================================
  // LOADING
  // =========================================================

  if (loading) {
    return (
      <AppLayout
        title="Child Monitoring"
        breadcrumb={[
          'Social Worker',
          'Child Monitoring',
        ]}
      >
        <div className="bg-white rounded-3xl border border-gray-100 p-10 text-center">
          <i className="ri-loader-4-line animate-spin text-3xl text-teal-500" />

          <p className="text-sm text-gray-500 mt-3">
            Loading child monitoring data...
          </p>
        </div>
      </AppLayout>
    );
  }

  // =========================================================
  // MAIN UI
  // =========================================================

  return (
    <AppLayout
      title="Child Monitoring"
      breadcrumb={[
        'Social Worker',
        'Child Monitoring',
      ]}
    >
      <div className="space-y-5">

        {/* ===================================================
            HEADER
        =================================================== */}

        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Child Monitoring
          </h1>

          <p className="text-sm text-gray-500 mt-1">
            Track each child's case progress,
            activity logs, referrals, and victim
            reports based on case number.
          </p>
        </div>

        {/* ===================================================
            STATISTICS
        =================================================== */}

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">

          <StatCard
            icon="ri-team-line"
            title="Total Children"
            value={String(
              totalChildren
            )}
            desc="Under your caseload"
            color="blue"
          />

          <StatCard
            icon="ri-checkbox-circle-line"
            title="Active Cases"
            value={String(
              activeCases
            )}
            desc="Currently in progress"
            color="green"
          />

          <StatCard
            icon="ri-alert-line"
            title="At Risk / High Risk"
            value={String(
              atRisk
            )}
            desc="Require attention"
            color="orange"
          />

          <StatCard
            icon="ri-user-voice-line"
            title="Victim Reports"
            value={String(
              victimReportsCount
            )}
            desc="Linked reports"
            color="purple"
          />

          <StatCard
            icon="ri-check-double-line"
            title="Closed Cases"
            value={String(
              closedCases
            )}
            desc="Total closed"
            color="emerald"
          />

        </div>

        {/* ===================================================
            CONTENT
        =================================================== */}

        <div className="grid grid-cols-1 xl:grid-cols-[410px,1fr] gap-5">

          {/* =================================================
              CHILD LIST
          ================================================= */}

          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">

            {/* TABS */}

            <div className="flex overflow-x-auto border-b border-gray-100">

              <button
                onClick={() =>
                  setActiveTab('all')
                }
                className={`px-5 py-4 text-sm whitespace-nowrap font-medium ${
                  activeTab === 'all'
                    ? 'text-teal-600 border-b-2 border-teal-500'
                    : 'text-gray-500'
                }`}
              >
                All Children (
                {totalChildren}
                )
              </button>

              <button
                onClick={() =>
                  setActiveTab('active')
                }
                className={`px-5 py-4 text-sm whitespace-nowrap font-medium ${
                  activeTab === 'active'
                    ? 'text-teal-600 border-b-2 border-teal-500'
                    : 'text-gray-500'
                }`}
              >
                Active (
                {activeCases}
                )
              </button>

              <button
                onClick={() =>
                  setActiveTab('risk')
                }
                className={`px-5 py-4 text-sm whitespace-nowrap font-medium ${
                  activeTab === 'risk'
                    ? 'text-teal-600 border-b-2 border-teal-500'
                    : 'text-gray-500'
                }`}
              >
                At Risk (
                {atRisk}
                )
              </button>

              {/* CLOSED TAB ONLY IF ADMIN HAS
                  AN ACTIVE CLOSED STATUS */}

              {hasActiveClosedStatus && (
                <button
                  onClick={() =>
                    setActiveTab('closed')
                  }
                  className={`px-5 py-4 text-sm whitespace-nowrap font-medium ${
                    activeTab === 'closed'
                      ? 'text-teal-600 border-b-2 border-teal-500'
                      : 'text-gray-500'
                  }`}
                >
                  Closed (
                  {closedCases}
                  )
                </button>
              )}

            </div>

            {/* SEARCH */}

            <div className="p-4 flex gap-3">

              <div className="flex-1 relative">

                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />

                <input
                  value={search}
                  onChange={(e) =>
                    setSearch(
                      e.target.value
                    )
                  }
                  type="text"
                  placeholder="Search child, case number, incident..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-2xl text-sm outline-none focus:ring-2 focus:ring-teal-100"
                />

              </div>

              <button
                onClick={fetchMonitoring}
                className="px-4 border border-gray-200 rounded-2xl text-gray-600 hover:bg-gray-50"
              >
                <i className="ri-refresh-line" />
              </button>

            </div>

            {/* CHILDREN */}

            <div className="space-y-2 px-3 pb-3 max-h-[720px] overflow-y-auto">

              {filteredChildren.length === 0 ? (

                <div className="p-8 text-center text-sm text-gray-400">
                  No child records found.
                </div>

              ) : (

                filteredChildren.map(
                  (child) => (
                    <button
                      key={
                        child.case_number
                      }
                      onClick={() =>
                        setSelectedChild(
                          child
                        )
                      }
                      className={`w-full text-left rounded-2xl border p-4 transition ${
                        selectedChild?.case_number ===
                        child.case_number
                          ? 'bg-teal-50 border-teal-100'
                          : 'bg-white border-gray-100 hover:bg-gray-50'
                      }`}
                    >

                      <div className="flex items-start gap-3">

                        {/* IMAGE */}

                        <div className="w-11 h-11 rounded-full bg-teal-100 flex items-center justify-center font-bold text-teal-700 text-sm overflow-hidden">

                          {getImageUrl(
                            child.profile_img
                          ) ? (

                            <img
                              src={
                                getImageUrl(
                                  child.profile_img
                                )!
                              }
                              alt={getFullName(
                                child
                              )}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display =
                                  'none';
                              }}
                            />

                          ) : (

                            getInitials(
                              child
                            )

                          )}

                        </div>

                        {/* CHILD INFO */}

                        <div className="flex-1 min-w-0">

                          <div className="flex items-center justify-between gap-2">

                            <h3 className="font-bold text-gray-800 truncate">
                              {getFullName(
                                child
                              )}
                            </h3>

                            <i className="ri-arrow-right-s-line text-gray-400" />

                          </div>

                          <p className="text-xs text-gray-400 mt-1">
                            {child.case_number}
                            {' • '}
                            {child.age || '-'}
                            {' yrs • '}
                            {child.gender || '-'}
                          </p>

                          <div className="flex items-center justify-between mt-3">

                            <div className="flex flex-wrap gap-2">

                              <div className="text-xs px-2 py-1 rounded-xl font-medium bg-orange-100 text-orange-700">
                                {child.incident_type ||
                                  'No incident'}
                              </div>

                              <div
                                className={`text-xs px-2 py-1 rounded-xl font-semibold ${getRiskColor(
                                  getRisk(
                                    child.incident_type
                                  ).riskLevel
                                )}`}
                              >
                                {getRisk(
                                  child.incident_type
                                ).riskLevel.toUpperCase()}
                              </div>

                            </div>

                            {/* STATUS */}

                            <div className="text-right">

                              <p className="text-[10px] text-gray-400">
                                Status
                              </p>

                              <p
                                className={`text-xs font-semibold px-2 py-1 rounded-xl inline-block ${getStatusColor(
                                  child.case_status
                                )}`}
                              >
                                {child.case_status ||
                                  'Pending'}
                              </p>

                            </div>

                          </div>

                        </div>

                      </div>

                    </button>
                  )
                )

              )}

            </div>

          </div>

          {/* =================================================
              SELECTED CHILD
          ================================================= */}

          {selectedChild ? (

            <div className="space-y-5">

              {/* =================================================
                  CHILD HEADER
              ================================================= */}

              <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5">

                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">

                  <div className="flex items-center gap-4">

                    {/* IMAGE */}

                    <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center font-bold text-teal-700 text-xl overflow-hidden">

                      {getImageUrl(
                        selectedChild.profile_img
                      ) ? (

                        <img
                          src={
                            getImageUrl(
                              selectedChild.profile_img
                            )!
                          }
                          alt={getFullName(
                            selectedChild
                          )}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            console.error(
                              'IMAGE FAILED:',
                              getImageUrl(
                                selectedChild.profile_img
                              )
                            );

                            e.currentTarget.style.display =
                              'none';
                          }}
                        />

                      ) : (

                        getInitials(
                          selectedChild
                        )

                      )}

                    </div>

                    {/* INFO */}

                    <div>

                      <h2 className="text-xl font-bold text-gray-900">
                        {getFullName(
                          selectedChild
                        )}
                      </h2>

                      <p className="text-sm text-gray-500 mt-1">
                        {selectedChild.case_number}
                        {' • '}
                        {selectedChild.age || '-'}
                        {' yrs • '}
                        {selectedChild.gender || '-'}
                      </p>

                      <div className="mt-3 flex flex-wrap items-center gap-2">

                        <span className="text-sm font-medium text-gray-600">
                          Type:
                        </span>

                        <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-xl text-xs font-semibold">
                          {selectedChild.incident_type}
                        </span>

                        <span
                          className={`px-3 py-1 rounded-xl text-xs font-semibold ${getRiskColor(
                            getRisk(
                              selectedChild.incident_type
                            ).riskLevel
                          )}`}
                        >
                          {getRisk(
                            selectedChild.incident_type
                          ).riskLevel.toUpperCase()}
                          {' • '}
                          {getRisk(
                            selectedChild.incident_type
                          ).riskScore}
                          %
                        </span>

                      </div>

                    </div>

                  </div>

                  <button
                    onClick={() =>
                      navigate(
                        `/child-profiles/${selectedChild.case_number}`
                      )
                    }
                    className="px-5 py-3 border border-gray-200 rounded-2xl text-sm font-semibold hover:bg-gray-50"
                  >
                    View Profile
                  </button>

                </div>

                {/* =================================================
                    PROGRESS OVERVIEW
                ================================================= */}

                <div className="mt-8">

                  <h3 className="font-bold text-gray-800 mb-6">
                    Case Progress Overview
                  </h3>

                  {loadingStatusTypes ? (

                    <div className="p-6 text-center text-sm text-gray-400">
                      Loading configured case statuses...
                    </div>

                  ) : (

                    <ProgressOverview
                      status={
                        selectedChild.case_status
                      }
                      statusTypes={
                        statusTypes
                      }
                    />

                  )}

                </div>

              </div>

              {/* =================================================
                  TIMELINE + SIDEBAR
              ================================================= */}

              <div className="grid grid-cols-1 xl:grid-cols-[1fr,300px] gap-5">

                <div className="space-y-5">

                  {/* TIMELINE */}

                  <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5">

                    <h3 className="font-bold text-gray-800 mb-6">
                      Case Timeline & Activity Log
                    </h3>

                    <div className="space-y-6">

                      {selectedChild.timeline?.length > 0 ? (

                        selectedChild.timeline.map(
                          (
                            item: any,
                            index: number
                          ) => (

                            <TimelineItem
                              key={index}
                              item={item}
                              isLast={
                                index ===
                                selectedChild.timeline.length -
                                  1
                              }
                            />

                          )
                        )

                      ) : (

                        <p className="text-sm text-gray-400">
                          No activity logs yet.
                        </p>

                      )}

                    </div>

                  </div>

                  {/* VICTIM REPORTS */}

                  <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5">

                    <h3 className="font-bold text-gray-800 mb-4">
                      Linked Victim Reports
                    </h3>

                    {selectedChild.victim_reports?.length > 0 ? (

                      <div className="space-y-3">

                        {selectedChild.victim_reports.map(
                          (report: any) => (

                            <div
                              key={
                                report.id
                              }
                              className="bg-gray-50 rounded-2xl p-4 border border-gray-100"
                            >

                              <div className="flex items-center justify-between gap-3">

                                <div>

                                  <p className="font-semibold text-gray-800">
                                    {report.report_uid}
                                  </p>

                                  <p className="text-xs text-gray-400">
                                    Reporter:{' '}
                                    {report.is_anonymous ===
                                    '1'
                                      ? 'Anonymous'
                                      : report.victim_name ||
                                        report.reporter_name ||
                                        'Unknown'}
                                  </p>

                                </div>

                                <span
                                  className={`text-xs px-2 py-1 rounded-full font-semibold ${getStatusColor(
                                    report.status
                                  )}`}
                                >
                                  {report.status}
                                </span>

                              </div>

                              <p className="text-sm text-gray-600 mt-3">
                                {report.description}
                              </p>

                              <p className="text-xs text-gray-400 mt-2">
                                {report.location}
                                {' • '}
                                {report.created_at}
                              </p>

                            </div>

                          )
                        )}

                      </div>

                    ) : (

                      <p className="text-sm text-gray-400">
                        No victim report linked to this case yet.
                      </p>

                    )}

                  </div>

                </div>

                {/* =================================================
                    SIDE CARDS
                ================================================= */}

                <div className="space-y-4">

                  <SideCard
                    title="Risk Level"
                    value={`${getRisk(
                      selectedChild.incident_type
                    ).riskLevel.toUpperCase()} • ${getRisk(
                      selectedChild.incident_type
                    ).riskScore}%`}
                    desc={
                      selectedChild.recommendation ||
                      `Risk assessment based on ${selectedChild.incident_type}`
                    }
                    icon="ri-alert-line"
                    color={
                      getRisk(
                        selectedChild.incident_type
                      ).riskLevel ===
                      'critical'
                        ? 'orange'
                        : getRisk(
                            selectedChild.incident_type
                          ).riskLevel === 'high'
                        ? 'purple'
                        : 'green'
                    }
                  />

                  <SideCard
                    title="Guardian"
                    value={
                      selectedChild.guardian_name ||
                      'No guardian'
                    }
                    desc={
                      selectedChild.guardian_contact ||
                      'No contact'
                    }
                    icon="ri-parent-line"
                    color="blue"
                  />

                  <SideCard
                    title="Location"
                    value={
                      selectedChild.barangay ||
                      'No barangay'
                    }
                    desc={`${selectedChild.municipality || ''}, ${
                      selectedChild.province || ''
                    }`}
                    icon="ri-map-pin-line"
                    color="green"
                  />

                  <SideCard
                    title="Assigned To"
                    value={
                      user?.name ||
                      'Social Worker'
                    }
                    desc="Social Worker"
                    icon="ri-user-heart-line"
                    color="purple"
                  />

                </div>

              </div>

            </div>

          ) : (

            <div className="bg-white rounded-3xl border border-gray-100 p-10 text-center text-gray-400">
              No selected child.
            </div>

          )}

        </div>

      </div>
    </AppLayout>
  );
}

// =============================================================
// PROGRESS OVERVIEW
// =============================================================

function ProgressOverview({
  status,
  statusTypes,
}: {
  status: string;
  statusTypes: StatusType[];
}) {
  const normalizeStatus = (
    value?: string
  ) => {
    return String(value || '')
      .trim()
      .toLowerCase();
  };

  // Only statuses currently ACTIVE in Admin
  const activeStatuses =
    statusTypes.filter(
      (item) => item.active
    );

  const normalizedCurrentStatus =
    normalizeStatus(status);

  const activeIndex =
    activeStatuses.findIndex(
      (item) =>
        normalizeStatus(
          item.name
        ) === normalizedCurrentStatus ||
        normalizeStatus(
          item.code
        ) === normalizedCurrentStatus
    );

  if (activeStatuses.length === 0) {
    return (
      <div className="p-6 rounded-2xl bg-gray-50 border border-gray-100 text-center">
        <p className="text-sm text-gray-500">
          No active case statuses are configured.
        </p>

        <p className="text-xs text-gray-400 mt-1">
          Please configure status types in Admin
          System Configuration.
        </p>
      </div>
    );
  }

  return (
    <div
      className={`grid grid-cols-2 ${
        activeStatuses.length >= 5
          ? 'md:grid-cols-5'
          : activeStatuses.length === 4
          ? 'md:grid-cols-4'
          : activeStatuses.length === 3
          ? 'md:grid-cols-3'
          : 'md:grid-cols-2'
      } gap-4`}
    >

      {activeStatuses.map(
        (step, index) => {

          const active =
            activeIndex >= 0
              ? index <= activeIndex
              : false;

          const current =
            activeIndex === index;

          return (
            <div
              key={
                step.code ||
                step.name
              }
              className={`
                rounded-2xl
                border
                p-5
                text-center
                transition-all
                ${
                  active
                    ? 'bg-teal-500 border-teal-500 text-white shadow-md'
                    : 'bg-white border-gray-200 text-gray-600'
                }
              `}
            >

              <div className="flex items-center justify-center mb-3">

                <div
                  className={`
                    w-10 h-10 rounded-full flex items-center justify-center text-lg
                    ${
                      active
                        ? 'bg-white/20 text-white'
                        : 'bg-gray-100 text-gray-500'
                    }
                  `}
                >

                  {active ? (

                    <i className="ri-check-line" />

                  ) : (

                    <i className="ri-time-line" />

                  )}

                </div>

              </div>

              <h3 className="font-bold text-sm md:text-base">
                {step.name}
              </h3>

              <p
                className={`
                  text-xs mt-2
                  ${
                    active
                      ? 'text-white/80'
                      : 'text-gray-400'
                  }
                `}
              >
                {current
                  ? 'Current Status'
                  : active
                  ? 'Completed'
                  : 'Pending'}
              </p>

            </div>
          );
        }
      )}

    </div>
  );
}

// =============================================================
// TIMELINE ITEM
// =============================================================

function TimelineItem({
  item,
  isLast,
}: {
  item: any;
  isLast: boolean;
}) {
  return (
    <div className="flex gap-4">

      <div className="flex flex-col items-center">

        <div className="w-8 h-8 rounded-full bg-teal-500 text-white flex items-center justify-center">
          <i className="ri-check-line" />
        </div>

        {!isLast && (
          <div className="w-0.5 flex-1 bg-gray-200 mt-2" />
        )}

      </div>

      <div className="flex-1 bg-gray-50 rounded-2xl p-4">

        <div className="flex flex-col md:flex-row md:items-start justify-between gap-2">

          <div>

            <h4 className="font-semibold text-gray-800">
              {item.source}: {item.title}
            </h4>

            <p className="text-sm text-gray-500 mt-1">
              {item.description}
            </p>

          </div>

          <span className="text-xs text-gray-400 whitespace-nowrap">
            {item.date}
          </span>

        </div>

      </div>

    </div>
  );
}

// =============================================================
// STAT CARD
// =============================================================

function StatCard({
  icon,
  title,
  value,
  desc,
  color,
}: {
  icon: string;
  title: string;
  value: string;
  desc: string;
  color: string;
}) {
  const colors: any = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    orange: 'bg-orange-100 text-orange-600',
    purple: 'bg-purple-100 text-purple-600',
    emerald: 'bg-emerald-100 text-emerald-600',
  };

  return (
    <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5">

      <div
        className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
          colors[color]
        }`}
      >
        <i className={`${icon} text-2xl`} />
      </div>

      <div className="mt-5">

        <p className="text-sm text-gray-500">
          {title}
        </p>

        <h2 className="text-3xl font-bold text-gray-900 mt-1">
          {value}
        </h2>

        <p className="text-sm text-gray-400 mt-1">
          {desc}
        </p>

      </div>

    </div>
  );
}

// =============================================================
// SIDE CARD
// =============================================================

function SideCard({
  title,
  value,
  desc,
  icon,
  color,
}: {
  title: string;
  value: string;
  desc: string;
  icon: string;
  color: string;
}) {
  const colors: any = {
    green: 'bg-green-100 text-green-600',
    orange: 'bg-orange-100 text-orange-600',
    blue: 'bg-blue-100 text-blue-600',
    purple: 'bg-purple-100 text-purple-600',
  };

  return (
    <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5">

      <div
        className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
          colors[color]
        }`}
      >
        <i className={`${icon} text-xl`} />
      </div>

      <div className="mt-4">

        <p className="text-sm text-gray-500">
          {title}
        </p>

        <h3 className="text-lg font-bold text-gray-900 mt-1">
          {value}
        </h3>

        <p className="text-sm text-gray-400 mt-1 line-clamp-4">
          {desc}
        </p>

      </div>

    </div>
  );
}